import { useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { Users, Building2, FileText, Banknote, Clock } from 'lucide-react';
import { motion } from 'motion/react';
import { TextMorph } from 'torph/react';
import { useCurrencySymbol } from '@/hooks/useCurrencySymbol';
import { useTilt } from '@/hooks/useTilt';
import type { AdminStats } from '../types';
import {
  ADMIN_COLORS,
  staggerContainer,
  staggerItem,
} from '../utils/adminAnimations';

interface AdminQuickStatsProps {
  stats: AdminStats;
}

const STAT_CARDS = [
  {
    key: 'therapists',
    icon: Users,
    labelKey: 'admin.activeEmployees',
    getValue: (s: AdminStats) => s.totalTherapists,
    format: 'number' as const,
  },
  {
    key: 'hospitals',
    icon: Building2,
    labelKey: 'admin.activeHospitals',
    getValue: (s: AdminStats) => s.totalHospitals,
    format: 'number' as const,
  },
  {
    key: 'reports',
    icon: FileText,
    labelKey: 'admin.reportsThisMonth',
    getValue: (s: AdminStats) => s.totalReportsThisMonth,
    format: 'number' as const,
  },
  {
    key: 'payroll',
    icon: Banknote,
    labelKey: 'admin.totalPayrollApproved',
    getValue: (s: AdminStats) => s.totalPayroll,
    format: 'currency' as const,
    link: '/admin/payroll',
  },
  {
    key: 'pending',
    icon: Clock,
    labelKey: 'admin.pendingApprovals',
    getValue: (s: AdminStats) => s.pendingApprovals,
    format: 'number' as const,
    highlight: (s: AdminStats) => s.pendingApprovals > 0,
  },
];

function AnimatedStatValue({ value, format }: { value: number; format: 'number' | 'currency' }) {
  const sym = useCurrencySymbol();
  const text = format === 'currency'
    ? `${value.toLocaleString()} ${sym}`
    : value.toLocaleString();
  return (
    <TextMorph as="span" duration={300} locale="he" respectReducedMotion>
      {text}
    </TextMorph>
  );
}

function StatCardWithTilt({
  card,
  stats,
  t,
  navigate,
}: {
  card: (typeof STAT_CARDS)[number];
  stats: AdminStats;
  t: (key: string) => string;
  navigate: (path: string) => void;
}) {
  const tiltRef = useRef<HTMLDivElement>(null);
  const { onMouseMove, onMouseLeave } = useTilt(tiltRef, { maxTilt: 4 });
  const Icon = card.icon;
  const value = card.getValue(stats);
  const isClickable = !!card.link;

  return (
    <motion.div
      variants={staggerItem}
      onClick={isClickable ? () => navigate(card.link!) : undefined}
    >
      <div
        ref={tiltRef}
        onMouseMove={onMouseMove}
        onMouseLeave={onMouseLeave}
        className={`rounded-3xl p-3 md:p-4 border border-white/30 transition-shadow glass-surface${isClickable ? ' cursor-pointer hover:shadow-sm' : ''}`}
      >
        <div className="flex items-center gap-2 mb-2">
          <Icon
            className="w-4 h-4 shrink-0"
            style={{ color: ADMIN_COLORS.navy + '80' }}
          />
          <p className="text-xs font-bold text-slate-500 uppercase">
            {t(card.labelKey)}
          </p>
        </div>
        <p className="text-2xl font-bold text-slate-800">
          <AnimatedStatValue value={value} format={card.format} />
        </p>
      </div>
    </motion.div>
  );
}

export function AdminQuickStats({ stats }: AdminQuickStatsProps) {
  const { t } = useTranslation();
  const navigate = useNavigate();

  return (
    <motion.div
      className="grid grid-cols-2 md:grid-cols-4 gap-3 lg:grid-cols-5"
      variants={staggerContainer}
      initial="hidden"
      animate="visible"
    >
      {STAT_CARDS.map((card) => (
        <StatCardWithTilt
          key={card.key}
          card={card}
          stats={stats}
          t={t}
          navigate={navigate}
        />
      ))}
    </motion.div>
  );
}
